import React, { useEffect, useState, useMemo, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { FaEdit, FaTrashAlt } from "react-icons/fa";
import { fetchUserStories, deleteUserStory } from "./userStoryService";
import { useResizableColumns } from "./issues/useResizableColumns";
import { SupportSortableHeader } from "./SupportSortableHeader";
import TablePagination, { DEFAULT_ROWS_PER_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE } from "../components/TablePagination";
import axiosInstance from "../components/api/axiosInstance";
import { useConfirm } from "../components/ConfirmModal";
import "../assets/css/Projects.css";
import "../assets/css/ViewCustomer.css";
import "../assets/css/UserStories.css";
import "../assets/css/DigixLeads.css";
import "../assets/css/Issues.css";

const USER_STORY_STATUSES = ["New", "In Progress", "Completed"];
const USER_STORY_PRIORITIES = ["High", "Medium", "Low"];
const USER_STORIES_ROWS_PER_PAGE_KEY = "userStoriesRowsPerPage";

/** Stable defaults — `projects = []` in params creates a new [] every render and retriggers useEffect (page blink). */
const EMPTY_PROJECTS = [];
const EMPTY_SUBPROJECTS = [];

const pickPriorityValue = (s) => {
  const v =
    s?.Priority ??
    s?.priority ??
    s?.Priority_Level ??
    s?.priority_level ??
    s?.Priority_Seq ??
    s?.priority_seq ??
    s?.PrioritySeq ??
    s?.prioritySeq ??
    s?.Priority_Id ??
    s?.priority_id ??
    "";

  if (v === null || v === undefined) return "";
  return String(v).trim();
};

function UserStoriesTable({
  projectId,
  subProjectId,
  projectName,
  subProjectName,
  onRefresh,
  /** Optional heading above filters (e.g. View Subproject page). */
  cardTitle,
  /** When false (e.g. PM tab hidden), skip API fetch — avoids redundant loads while another tab is active. */
  dataActive = true,
  /**
   * Controls which level of stories to fetch:
   * - "project": only project-level stories (Sub_Project_Id = null)
   * - "subproject": only stories for the provided subProjectId
   * - "all": current existing behavior (project-level + all subproject stories when "All Subprojects")
   */
  storiesScope = "all",
  // Optional controls: show Project/Subproject filters inside this table header row
  projects = EMPTY_PROJECTS,
  subprojects = EMPTY_SUBPROJECTS,
  loadingSubprojects = false,
  selectedProjectId,
  selectedSubprojectId,
  onProjectChange,
  onSubprojectChange
}) {
  const navigate = useNavigate();
  const location = useLocation();
  const confirm = useConfirm();
  const [userStories, setUserStories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deletingId, setDeletingId] = useState(null);
  const [storyPage, setStoryPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(() => {
    try {
      const saved = localStorage.getItem(USER_STORIES_ROWS_PER_PAGE_KEY);
      const parsed = parseInt(saved, 10);
      if (DEFAULT_ROWS_PER_PAGE_OPTIONS.includes(parsed)) {
        return parsed;
      }
    } catch {
      /* ignore */
    }
    return DEFAULT_ROWS_PER_PAGE;
  });
  const [statusFilter, setStatusFilter] = useState("");
  const [titleFilter, setTitleFilter] = useState("");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [freeSearch, setFreeSearch] = useState("");
  const [sortColumn, setSortColumn] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const priorityCacheRef = useRef({});
  const subprojectsCacheRef = useRef({});
  const USER_STORY_COL_DEFAULT_WIDTHS = [160, 200, 100, 120, 120, 120, 100];
  const { getColProps, ResizeHandle } = useResizableColumns(USER_STORY_COL_DEFAULT_WIDTHS);

  const mergePriorityCache = (stories) => {
    const cache = priorityCacheRef.current;
    if (!stories || Object.keys(cache).length === 0) return stories;
    return stories.map((s) => {
      const id = s.User_Story_Id ?? s.user_story_id;
      const cached = cache[id];
      const hasPriority = pickPriorityValue(s) !== "";
      if (cached != null && String(cached).trim() !== "" && !hasPriority) {
        return { ...s, Priority: cached };
      }
      return s;
    });
  };

  const fetchSubprojectsForProject = async (pid) => {
    if (!pid) return [];
    const cacheHit = subprojectsCacheRef.current[pid];
    if (cacheHit) return cacheHit;

    // If parent already provided subprojects for the current specific project, reuse them.
    // (When user selects "All Projects", parent usually won't provide any.)
    if (selectedProjectId && String(pid) === String(selectedProjectId) && Array.isArray(subprojects) && subprojects.length > 0) {
      subprojectsCacheRef.current[pid] = subprojects;
      return subprojects;
    }

    try {
      // Primary endpoint
      let response = await axiosInstance.get(`/sub-projects/project/${pid}`);
      let raw = response.data;

      let arr = [];
      let primaryParsed = false;
      if (Array.isArray(raw)) {
        arr = raw;
        primaryParsed = true;
      } else if (raw && Array.isArray(raw.sub_projects)) {
        arr = raw.sub_projects;
        primaryParsed = true;
      } else if (raw && Array.isArray(raw.data)) {
        arr = raw.data;
        primaryParsed = true;
      }

      // Fallback legacy endpoint:
      // Only when primary response shape is not what we expect.
      // If primary returns a valid empty array, don't call legacy endpoint.
      if (!primaryParsed) {
        try {
          response = await axiosInstance.get(`/projects/${pid}/sub-projects`);
          raw = response.data;
          arr = Array.isArray(raw) ? raw : raw?.sub_projects ?? raw?.data ?? [];
        } catch {
          arr = [];
        }
      }

      const mapped = (arr || [])
        .map((sp) => ({
          id: sp.Sub_Project_Id ?? sp.sub_project_id ?? sp.id,
          name: sp.Sub_Project_Name ?? sp.sub_project_name ?? sp.name ?? ""
        }))
        .filter((x) => x.id != null && String(x.name).trim() !== "");

      mapped.sort((a, b) => String(a.name).localeCompare(String(b.name)));
      subprojectsCacheRef.current[pid] = mapped;
      return mapped;
    } catch (err) {
      console.error("Error fetching subprojects for project:", pid, err);
      subprojectsCacheRef.current[pid] = [];
      return [];
    }
  };

  const loadUserStories = async () => {
    const projectSelectValue = selectedProjectId != null ? String(selectedProjectId) : (projectId != null ? String(projectId) : "");
    const subprojectSelectValue = selectedSubprojectId != null ? String(selectedSubprojectId) : (subProjectId != null ? String(subProjectId) : "");

    const allProjectsSelected = projectSelectValue === "";
    const allSubprojectsSelected = subprojectSelectValue === "";

    const projectIdsToLoad = allProjectsSelected
      ? (projects || []).map((p) => p.Project_Id ?? p.project_id ?? p.id).filter((id) => id != null)
      : [Number(projectSelectValue)].filter((n) => Number.isFinite(n) && n > 0);

    if (!projectIdsToLoad.length) {
      setUserStories([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const loadForProject = async (pid) => {
        if (storiesScope === "project") {
          // Project-level only
          const projectLevelStories = await fetchUserStories(pid, undefined);
          return projectLevelStories || [];
        }

        if (storiesScope === "subproject") {
          // Subproject-level only
          const sid = Number(subprojectSelectValue);
          if (!Number.isFinite(sid) || sid <= 0) return [];
          const stories = await fetchUserStories(pid, sid);
          return stories || [];
        }

        // "all" scope: existing behavior
        // Specific subproject selected: show only those stories
        if (!allSubprojectsSelected) {
          const sid = Number(subprojectSelectValue);
          if (!Number.isFinite(sid) || sid <= 0) return [];
          const stories = await fetchUserStories(pid, sid);
          return stories || [];
        }

        // All subprojects: project-level + each subproject's stories (parallelize independent calls)
        const results = [];
        const [projectLevelStories, subList] = await Promise.all([
          fetchUserStories(pid, undefined),
          fetchSubprojectsForProject(pid)
        ]);
        results.push(projectLevelStories || []);
        if (subList && subList.length > 0) {
          const subStoriesResults = await Promise.all(
            subList.map(async (sp) => {
              const sid = Number(sp.id);
              if (!Number.isFinite(sid) || sid <= 0) return [];
              try {
                const s = await fetchUserStories(pid, sid);
                return s || [];
              } catch {
                return [];
              }
            })
          );
          for (const arr of subStoriesResults) results.push(arr);
        }

        return results.flat();
      };

      const storiesPerProject = await Promise.all(projectIdsToLoad.map((pid) => loadForProject(pid)));
      const merged = storiesPerProject.flat();
      setUserStories(mergePriorityCache(merged || []));
    } catch (err) {
      console.error("Error fetching user stories:", err);
      setUserStories([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!dataActive) return;
    loadUserStories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataActive, selectedProjectId, selectedSubprojectId, projectId, subProjectId, projects, subprojects, storiesScope]);

  // After returning from Add User Story page, refresh so the new row appears (replaces prior in-modal refresh).
  useEffect(() => {
    try {
      if (sessionStorage.getItem("refresh_user_stories_list") !== "1") return;
      sessionStorage.removeItem("refresh_user_stories_list");
      if (onRefresh) onRefresh();
      else void loadUserStories();
    } catch {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const filteredStories = useMemo(() => {
    return userStories.filter((s) => {
      const title = (s.Title || s.title || "").toLowerCase();
      const status = (s.Status || s.status || "").toLowerCase();
      const priority = pickPriorityValue(s).toLowerCase();
      const matchesTitle = !titleFilter.trim() || title.includes(titleFilter.trim().toLowerCase());
      const matchesStatus = !statusFilter || status === statusFilter.toLowerCase();
      const matchesPriority = !priorityFilter || priority === priorityFilter.toLowerCase();
      return matchesTitle && matchesStatus && matchesPriority;
    });
  }, [userStories, statusFilter, titleFilter, priorityFilter]);

  const freeSearchFilteredStories = useMemo(() => {
    const q = String(freeSearch || "").trim().toLowerCase();
    if (!q) return filteredStories;
    return (filteredStories || []).filter((s) => {
      const bag = [
        s?.Title ?? s?.title ?? "",
        s?.Description ?? s?.description ?? "",
        pickPriorityValue(s),
        s?.Status ?? s?.status ?? "",
        s?.Created_By_Name ?? s?.created_by_name ?? "",
        s?.Created_On ?? s?.created_on ?? ""
      ].map((v) => String(v == null ? "" : v).toLowerCase());
      return bag.some((v) => v.includes(q));
    });
  }, [filteredStories, freeSearch]);

  const getUserStorySortValue = (story, columnId) => {
    const rawDate = (value) => {
      if (!value) return 0;
      const ts = new Date(value).getTime();
      return Number.isFinite(ts) ? ts : 0;
    };
    switch (columnId) {
      case "title":
        return String(story?.Title ?? story?.title ?? "").toLowerCase();
      case "description":
        return String(story?.Description ?? story?.description ?? "").toLowerCase();
      case "priority":
        return String(pickPriorityValue(story) || "").toLowerCase();
      case "status":
        return String(story?.Status ?? story?.status ?? "").toLowerCase();
      case "createdBy":
        return String(story?.Created_By_Name ?? story?.created_by_name ?? "").toLowerCase();
      case "createdOn":
        return rawDate(story?.Created_On ?? story?.created_on);
      default:
        return "";
    }
  };

  const sortedStories = useMemo(() => {
    if (!sortColumn) return freeSearchFilteredStories;
    const mult = sortDirection === "desc" ? -1 : 1;
    return [...freeSearchFilteredStories].sort((a, b) => {
      const va = getUserStorySortValue(a, sortColumn);
      const vb = getUserStorySortValue(b, sortColumn);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      const ida = Number(a?.User_Story_Id ?? a?.user_story_id ?? 0);
      const idb = Number(b?.User_Story_Id ?? b?.user_story_id ?? 0);
      return (ida - idb) * mult;
    });
  }, [freeSearchFilteredStories, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedStories.length / rowsPerPage));
  const startIdx = (storyPage - 1) * rowsPerPage;
  const currentStories = sortedStories.slice(startIdx, startIdx + rowsPerPage);

  useEffect(() => {
    if (storyPage > totalPages && totalPages > 0) {
      setStoryPage(1);
    }
  }, [storyPage, totalPages]);

  const handleRowsPerPageChange = (next) => {
    setRowsPerPage(next);
    setStoryPage(1);
    localStorage.setItem(USER_STORIES_ROWS_PER_PAGE_KEY, String(next));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    try {
      const d = new Date(dateStr);
      return isNaN(d.getTime())
        ? "—"
        : d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
    } catch {
      return "—";
    }
  };

  const handleAddUserStoryClick = () => {
    const proj =
      selectedProjectId != null && String(selectedProjectId) !== ""
        ? String(selectedProjectId)
        : projectId != null
          ? String(projectId)
          : "";
    const sub =
      selectedSubprojectId != null && String(selectedSubprojectId) !== ""
        ? String(selectedSubprojectId)
        : subProjectId != null
          ? String(subProjectId)
          : "";
    const navState = {
      returnToPath: location.pathname,
      returnToSearch: location.search || "",
      returnToState: null
    };
    if (!proj) {
      navigate("/projects/user-stories/add", { state: navState });
      return;
    }
    if (sub) {
      navigate(`/projects/${proj}/subprojects/${sub}/user-stories/add`, { state: navState });
    } else {
      navigate(`/projects/${proj}/user-stories/add`, { state: navState });
    }
  };

  const handleDeleteStory = async (storyId) => {
    if (!(await confirm({ title: "Delete User Story", message: "Are you sure you want to delete this user story?" }))) return;
    setDeletingId(storyId);
    try {
      await deleteUserStory(storyId);
      setUserStories((prev) => prev.filter((s) => (s.User_Story_Id ?? s.user_story_id) !== storyId));
      if (onRefresh) onRefresh();
    } catch (err) {
      console.error("Error deleting user story:", err);
      alert(err.response?.data?.message || err.response?.data?.error || "Failed to delete user story.");
    } finally {
      setDeletingId(null);
    }
  };

  const handleSort = (columnId) => {
    setStoryPage(1);
    if (sortColumn === columnId) {
      setSortDirection((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(columnId);
      setSortDirection("asc");
    }
  };

  const projectSelectValue = selectedProjectId != null ? String(selectedProjectId) : (projectId != null ? String(projectId) : "");
  const subprojectSelectValue = selectedSubprojectId != null ? String(selectedSubprojectId) : (subProjectId != null ? String(subProjectId) : "");
  const allProjectsSelected = projectSelectValue === "";
  const allSubprojectsSelected = subprojectSelectValue === "";

  return (
    <div className="userstories-table-panel">
      {cardTitle ? (
        <h3 className="viewlead-section-title" style={{ marginTop: 0, marginBottom: 16 }}>
          {cardTitle}
        </h3>
      ) : null}
      <div className="projects-header-row" style={{ marginBottom: 12 }}>
        <div className="userstories-filters-row projects-filters-row" style={{ flex: 1, marginBottom: 0 }}>
          <input
            type="text"
            className="userstories-title-search"
            placeholder="Search by title..."
            value={titleFilter}
            onChange={(e) => {
              setTitleFilter(e.target.value);
              setStoryPage(1);
            }}
          />

          <select
            className="userstories-project-filter"
            value={projectSelectValue}
            onChange={(e) => {
              setStoryPage(1);
              setTitleFilter("");
              setStatusFilter("");
              setPriorityFilter("");
              const nextProjectValue = e.target.value;
              if (onProjectChange) onProjectChange(nextProjectValue);
              // When switching to "All Projects", also reset to "All Subprojects"
              if (onSubprojectChange && nextProjectValue === "") onSubprojectChange("");
            }}
          >
            <option value="">All Projects</option>
            {(projects || []).map((p) => {
              const id = p.Project_Id ?? p.project_id ?? p.id;
              const name = p.Project_Name ?? p.project_name ?? p.name ?? "";
              return (
                <option key={id} value={String(id)}>
                  {name || `Project ${id}`}
                </option>
              );
            })}
          </select>

          <select
            className="userstories-subproject-filter"
            value={subprojectSelectValue}
            onChange={(e) => {
              setStoryPage(1);
              setTitleFilter("");
              setStatusFilter("");
              setPriorityFilter("");
              if (onSubprojectChange) onSubprojectChange(e.target.value);
            }}
            disabled={loadingSubprojects || allProjectsSelected}
          >
            <option value="">All Subprojects</option>
            {(subprojects || []).map((sp) => (
              <option key={sp.id} value={String(sp.id)}>
                {sp.name}
              </option>
            ))}
          </select>
          <select
            className="userstories-priority-filter"
            value={priorityFilter}
            onChange={(e) => {
              setPriorityFilter(e.target.value);
              setStoryPage(1);
            }}
          >
            <option value="">All Priority</option>
            {USER_STORY_PRIORITIES.map((p) => (
              <option key={p} value={p}>
                {p}
              </option>
            ))}
          </select>
          <select
            className="userstories-status-filter"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setStoryPage(1);
            }}
          >
            <option value="">All Status</option>
            {USER_STORY_STATUSES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
          <button
            type="button"
            className="btn-clear"
            onClick={() => {
              setTitleFilter("");
              setStatusFilter("");
              setPriorityFilter("");
              setStoryPage(1);
              if (onProjectChange) onProjectChange("");
              if (onSubprojectChange) onSubprojectChange("");
            }}
            style={{ marginLeft: 8 }}
          >
            Clear
          </button>
        </div>
        <div className="header-actions">
          <button type="button" className="btn-add-lead" onClick={handleAddUserStoryClick}>
            Add User Story
          </button>
        </div>
      </div>

      <div className="search-row-2">
        <div className="free-search-bar" style={{ marginLeft: "auto", maxWidth: 280 }}>
          <input
            type="text"
            className="free-search-input"
            placeholder="Search..."
            value={freeSearch}
            onChange={(e) => {
              setFreeSearch(e.target.value);
              setStoryPage(1);
            }}
            title="Search across user story table columns"
          />
        </div>
      </div>

      {loading ? (
        <div className="project-subprojects-loading">Loading user stories...</div>
      ) : (
        <>
          <div className="projects-container userstories-table-container">
            <div className="table-wrapper userstories-table-wrapper">
              <div className="table-container">
                <table className="data-table projects-table-frozen">
                  <thead>
                    <tr>
                      <th className="col-sticky col-0 projects-th-sortable" {...getColProps(0)}>
                        <div className="projects-th-sort-row">
                          <SupportSortableHeader columnId="title" label="Title" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={0} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(1)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="description" label="Description" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={1} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(2)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="priority" label="Priority" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={2} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(3)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="status" label="Status" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={3} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(4)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="createdBy" label="Created By" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={4} />
                      </th>
                      <th className="projects-th-sortable" {...getColProps(5)}>
                        <div className="projects-th-sort-row projects-th-sort-row--center">
                          <SupportSortableHeader columnId="createdOn" label="Created On" sortColumn={sortColumn} sortDirection={sortDirection} onSort={handleSort} />
                        </div>
                        <ResizeHandle index={5} />
                      </th>
                      <th className="col-sticky col-action" {...getColProps(6)}>
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedStories.length === 0 ? (
                      <tr>
                        <td colSpan="7" className="userstories-empty-cell">
                          No user stories yet.
                        </td>
                      </tr>
                    ) : (
                      currentStories.map((story) => {
                        const id = story.User_Story_Id || story.user_story_id;
                        return (
                          <tr key={id}>
                            <td className="col-sticky col-0">
                              <span
                                className="table-name-link"
                                role="button"
                                tabIndex={0}
                                onClick={() => {
                                  const storyProjectId =
                                    story.Project_Id ?? story.project_id ?? projectId;
                                  const storySubProjectId =
                                    story.Sub_Project_Id ?? story.sub_project_id ?? null;

                                  const hasSub = storySubProjectId != null && String(storySubProjectId) !== "";
                                  if (!storyProjectId) return;
                                  const viewPath = hasSub
                                    ? `/projects/${storyProjectId}/subprojects/${storySubProjectId}/user-stories/${id}/view`
                                    : `/projects/${storyProjectId}/user-stories/${id}/view`;
                                  navigate(viewPath);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === "Enter" || e.key === " ") {
                                    e.preventDefault();
                                    const storyProjectId =
                                      story.Project_Id ?? story.project_id ?? projectId;
                                    const storySubProjectId =
                                      story.Sub_Project_Id ?? story.sub_project_id ?? null;
                                    const hasSub = storySubProjectId != null && String(storySubProjectId) !== "";
                                    if (!storyProjectId) return;
                                    const viewPath = hasSub
                                      ? `/projects/${storyProjectId}/subprojects/${storySubProjectId}/user-stories/${id}/view`
                                      : `/projects/${storyProjectId}/user-stories/${id}/view`;
                                    navigate(viewPath);
                                  }
                                }}
                              >
                                {story.Title || story.title || "—"}
                              </span>
                            </td>
                            <td style={{ textAlign: "left", maxWidth: 300 }}>
                              <span
                                style={{
                                  display: "block",
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                  maxWidth: 300
                                }}
                                title={story.Description || story.description || ""}
                              >
                                {story.Description || story.description || "—"}
                              </span>
                            </td>
                            <td>
                              {(() => {
                                const priority = pickPriorityValue(story);
                                const priorityClass = priority
                                  ? `status-${String(priority).toLowerCase().replace(/\s/g, "-")}`
                                  : "status-not-set";
                                return (
                                  <span className={`status-badge ${priorityClass}`}>
                                    {priority || "Not Set"}
                                  </span>
                                );
                              })()}
                            </td>
                            <td>
                              <span
                                className={`status-badge status-${(story.Status || story.status || "")
                                  .toLowerCase()
                                  .replace(/\s/g, "-")}`}
                              >
                                {story.Status || story.status || "—"}
                              </span>
                            </td>
                            <td>{story.Created_By_Name || story.created_by_name || "—"}</td>
                            <td>{formatDate(story.Created_On || story.created_on)}</td>
                            <td className="col-sticky col-action">
                              <div className="project-actions-cell">
                                <button
                                  type="button"
                                  className="project-action-btn project-action-btn-icon project-action-btn-edit"
                                  title="Edit user story"
                                  onClick={() => {
                                    const storyProjectId =
                                      story.Project_Id ?? story.project_id ?? projectId;
                                    const storySubProjectId =
                                      story.Sub_Project_Id ?? story.sub_project_id ?? null;
                                    const hasSub = storySubProjectId != null && String(storySubProjectId) !== "";
                                    if (!storyProjectId) return;
                                    const editPath = hasSub
                                      ? `/projects/${storyProjectId}/subprojects/${storySubProjectId}/user-stories/${id}/edit`
                                      : `/projects/${storyProjectId}/user-stories/${id}/edit`;
                                    navigate(editPath);
                                  }}
                                >
                                  <FaEdit />
                                </button>
                                <button
                                  type="button"
                                  className="project-action-btn project-action-btn-icon project-action-btn-deactivate"
                                  title="Delete user story"
                                  disabled={deletingId === id}
                                  onClick={() => handleDeleteStory(id)}
                                >
                                  <FaTrashAlt />
                                </button>
                              </div>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
            {!loading && sortedStories.length > 0 ? (
              <TablePagination
                currentPage={storyPage}
                totalItems={sortedStories.length}
                rowsPerPage={rowsPerPage}
                rowsPerPageOptions={DEFAULT_ROWS_PER_PAGE_OPTIONS}
                onPageChange={setStoryPage}
                onRowsPerPageChange={handleRowsPerPageChange}
                itemLabel="user stories"
                rowsPerPageSelectId="user-stories-rows-per-page"
              />
            ) : null}
          </div>
        </>
      )}

    </div>
  );
}

export default UserStoriesTable;

